<style lang="scss" src="./permission-request.scss"></style>

<template>
  <div
    v-if="launcher === 'float'"
    class="consultation-invite__launcher"
    role="button"
    tabindex="0"
    aria-label="Учебная деятельность — оставить заявку"
    @click="showDrawer"
    @keydown.enter.prevent="showDrawer"
    @keydown.space.prevent="showDrawer"
  >
    <div class="consultation-invite__icon-wrap">
      <img
        :src="`${ICONS_BASE}/book-education.svg`"
        alt=""
        class="consultation-invite__icon consultation-invite__icon--book"
      />
    </div>
    <div class="consultation-invite__lead">
      <p class="consultation-invite__title">Образовательная деятельность</p>
      <p class="consultation-invite__subtitle">Оставьте заявку</p>
    </div>
  </div>
  <div v-else @click="showDrawer">
    <div class="btn btn-primary">Подать заявку</div>
  </div>

  <a-drawer
    :key="drawerRenderKey"
    :open="open"
    class="permission-request-drawer"
    root-class-name="permission-request-drawer"
    :destroy-on-close="true"
    @after-open-change="onAfterOpenChange"
    @close="onClose"
  >
    <template #title>
      <div class="permission-request-drawer__header-container">
        <span class="permission-request-drawer__header-container--title">Заявка на разрешение</span>
        <span class="permission-request-drawer__header-container--description"
          >Ведение образовательной деятельности на территории ИНТЦ МГУ «Воробьевы горы»</span
        >
      </div>
    </template>

    <template #closeIcon>
      <span class="permission-request-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <div class="permission-request-drawer__section">
      <span class="permission-request-drawer__section-title">1. Общее</span>

      <a-form
        ref="generalFormRef"
        :model="form"
        :rules="rules"
        layout="vertical"
        class="permission-request-drawer__form"
      >
        <div class="permission-request-drawer__section-content-container">
          <a-form-item
            class="stc-ant-field-skin"
            label="Полное название юр. лица или ИП"
            name="fullLegalName"
            required
          >
            <a-input v-model:value="form.fullLegalName" placeholder="Полное название" />
          </a-form-item>

          <div class="permission-request-drawer__row">
            <a-form-item
              class="stc-ant-field-skin"
              label="Сокращенное название юр. лица или ИП"
              name="shortLegalName"
              required
            >
              <a-input v-model:value="form.shortLegalName" placeholder="Сокращенное название" />
            </a-form-item>

            <a-form-item
              class="stc-ant-field-skin"
              label="Организационно-правовая форма"
              name="organizationalLegalForm"
              required
            >
              <a-input v-model:value="form.organizationalLegalForm" placeholder="ООО, АО и т.д." />
            </a-form-item>
          </div>

          <a-form-item
            class="stc-ant-field-skin"
            label="Адрес местонахождения"
            name="locationAddress"
            required
          >
            <a-input v-model:value="form.locationAddress" placeholder="Фактический адрес" />
          </a-form-item>

          <div class="permission-request-drawer__row">
            <a-form-item
              class="stc-ant-field-skin"
              label="ОГРН/ОГРНИП"
              name="ogrnOrOgrnip"
              required
            >
              <a-input
                v-model:value="form.ogrnOrOgrnip"
                placeholder="Номер записи о создании юр. лица или ИП"
              />
            </a-form-item>

            <a-form-item
              class="stc-ant-field-skin"
              label="Дата регистрации"
              name="registrationDate"
              required
            >
              <a-date-picker
                v-model:value="form.registrationDate"
                class="permission-request-drawer__date"
                :locale="pickerLocale"
                format="DD.MM.YYYY"
                placeholder="ДД.ММ.ГГГГ"
                style="width: 100%"
              />
            </a-form-item>
          </div>

          <a-form-item
            class="stc-ant-field-skin"
            label="Наименование органа осуществляющего государственную регистрацию"
            name="registrationAuthority"
            required
          >
            <a-input v-model:value="form.registrationAuthority" placeholder="МВД, ФНС и т.д." />
          </a-form-item>

          <a-form-item
            class="stc-ant-field-skin"
            label="Виды, уровни образования, планируемые к осуществлению на территории ИНТЦ"
            name="educationTypesAndLevels"
            required
          >
            <a-select
              v-model:value="form.educationTypesAndLevels"
              mode="multiple"
              allow-clear
              show-arrow
              option-label-prop="label"
              popup-class-name="permission-request-drawer__multiselect-dropdown"
              class="permission-request-drawer__select-multiple"
              placeholder="Не выбраны"
            >
              <a-select-option
                v-for="opt in educationTypesAndLevelsOptions"
                :key="opt.value"
                :value="opt.value"
                :label="opt.label"
              >
                <span class="permission-request-drawer__multiselect-option">
                  <span
                    class="permission-request-drawer__multiselect-option-checkbox"
                    aria-hidden="true"
                  />
                  <span class="permission-request-drawer__multiselect-option-label">{{
                    opt.label
                  }}</span>
                </span>
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item
            class="stc-ant-field-skin"
            label="Формы реализации образовательной деятельности на территории ИНТЦ"
            name="educationActivityFormats"
            required
          >
            <a-checkbox-group
              v-model:value="form.educationActivityFormats"
              class="permission-request-drawer__checkbox-group"
            >
              <div class="permission-request-drawer__checkbox-group-row">
                <a-checkbox
                  v-for="option in educationActivityFormatOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </a-checkbox>
              </div>
            </a-checkbox-group>
          </a-form-item>
        </div>
      </a-form>
    </div>

    <a-divider class="permission-request-drawer__divider" />

    <div class="permission-request-drawer__section">
      <span class="permission-request-drawer__section-title">1. Контактное лицо</span>

      <a-form
        ref="contactFormRef"
        :model="form"
        :rules="rules"
        layout="vertical"
        class="permission-request-drawer__form"
      >
        <div class="permission-request-drawer__section-content-container">
          <a-form-item class="stc-ant-field-skin" label="ФИО" name="contactFullName" required>
            <a-input v-model:value="form.contactFullName" placeholder="Полное имя" />
          </a-form-item>

          <div class="permission-request-drawer__row">
            <FormPhoneField v-model:model-value="form.phone" />

            <a-form-item class="stc-ant-field-skin" label="Почта" name="contactEmail" required>
              <a-input v-model:value="form.contactEmail" placeholder="example@mail.com" />
            </a-form-item>
          </div>

          <a-form-item
            class="stc-ant-field-skin"
            label="Описание проекта"
            name="educationProgramDescription"
            required
          >
            <a-textarea
              v-model:value="form.educationProgramDescription"
              class="permission-request-drawer__textarea"
              :auto-size="{ minRows: 4, maxRows: 8 }"
              :maxlength="EDUCATION_PROGRAM_DESCRIPTION_MAX_LENGTH"
              placeholder="Тип проекта, ожидаемые результаты"
              show-count
            />
          </a-form-item>
        </div>
      </a-form>
    </div>

    <a-divider class="permission-request-drawer__divider" />

    <div class="permission-request-drawer__section">
      <span class="permission-request-drawer__section-title">3. Документы</span>

      <div class="permission-request-drawer__section-content-container">
        <FormFileUpload ref="documentsUploadRef" v-model="documentItems" />
      </div>
    </div>

    <a-divider class="booking-infrastructure-drawer__mobile-divider" />

    <FormPrivacyConsentField
      v-model:model-value="form.privacy"
      class="booking-infrastructure-drawer__mobile-policy"
    />

    <template #footer>
      <div class="permission-request-drawer__footer">
        <FormPrivacyConsentField
          v-model:model-value="form.privacy"
          class="permission-request-drawer__footer-policy"
        />

        <div class="permission-request-drawer__buttons-container">
          <button type="button" class="btn btn-secondary btn-medium btn-cancel" @click="onClose">
            Отмена
          </button>
          <button
            type="button"
            class="btn btn-primary btn-medium"
            @click="onSubmit"
            :disabled="isSubmitDisabled"
          >
            {{ isSubmitting ? 'Отправка…' : 'Отправить' }}
          </button>
        </div>
      </div>
    </template>
  </a-drawer>

  <ConsultationRequestSuccessModal
    v-model:open="openModal"
    :description="successModalDescription"
  />
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    launcher?: 'button' | 'float';
  }>(),
  {
    launcher: 'button',
  },
);

const EDUCATION_PERMISSION_OPEN_EVENT = 'stc:education-permission-open';
import { CloseOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/ru';
import ruRu from 'ant-design-vue/es/date-picker/locale/ru_RU';
import { type FormSubjectOption } from '@/js/components/form/form-subject-field.vue';
import ConsultationRequestSuccessModal from '@/js/components/consultation-request-success-modal/consultation-request-success-modal.vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { submitFormWithCaptcha } from '@/js/utils/submitFormWithCaptcha';
import { getRecaptchaErrorMessage } from '@/js/utils/getRecaptchaToken';
import FormPrivacyConsentField from '@/js/components/form/form-privacy-consent-field.vue';
import FormPhoneField from '@/js/components/form/form-phone-field.vue';
import FormFileUpload, { type FileUploadItem } from '@/js/components/form/form-file-upload.vue';
import { trackYaGoal } from '@/js/utils/yandex-metrika';

const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

const pickerLocale = ruRu;
dayjs.locale('ru');
const EDUCATION_PROGRAM_DESCRIPTION_MAX_LENGTH = 512;

const EDUCATION_FORM_ENDPOINT = getApiUrl('/api/form/education/');

interface EducationFormMetaItem {
  id: string;
  text: string;
}

interface EducationFormMetaResponse {
  status?: string;
  msg?: {
    education_types?: EducationFormMetaItem[];
    implementation_form?: EducationFormMetaItem[];
    privacy?: EducationFormMetaItem[];
  };
  error?: string;
}

function mapMetaToOptions(items: EducationFormMetaItem[] | undefined): FormSubjectOption[] {
  return (items ?? []).map((item) => ({ value: item.id, label: item.text }));
}

const educationTypesAndLevelsOptions = ref<FormSubjectOption[]>([]);
const educationActivityFormatOptions = ref<FormSubjectOption[]>([]);
const privacyOptionId = ref('');

const openModal = ref<boolean>(false);
const successModalDescription = ref('');
const isSubmitting = ref(false);
const generalFormRef = ref<FormInstance>();
const contactFormRef = ref<FormInstance>();
const documentsUploadRef = ref<InstanceType<typeof FormFileUpload> | null>(null);
const drawerContentRef = ref<HTMLElement | null>(null);
const documentItems = ref<FileUploadItem[]>([]);
const open = ref<boolean>(false);
const drawerRenderKey = ref(0);

const initialFormState = () => ({
  fullLegalName: '',
  shortLegalName: '',
  organizationalLegalForm: '',
  locationAddress: '',
  ogrnOrOgrnip: '',
  registrationDate: null as Dayjs | null,
  registrationAuthority: '',
  educationTypesAndLevels: [] as string[],
  educationActivityFormats: [] as string[],
  contactFullName: '',
  phone: '',
  contactEmail: '',
  educationProgramDescription: '',
  privacy: false,
});

const form = reactive(initialFormState());

const isNotBlankString = (value: string): boolean => value.trim().length > 0;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isRequiredFieldsFilled = computed(
  () =>
    isNotBlankString(form.fullLegalName) &&
    isNotBlankString(form.shortLegalName) &&
    isNotBlankString(form.organizationalLegalForm) &&
    isNotBlankString(form.locationAddress) &&
    isNotBlankString(form.ogrnOrOgrnip) &&
    form.registrationDate !== null &&
    isNotBlankString(form.registrationAuthority) &&
    form.educationTypesAndLevels.length > 0 &&
    form.educationActivityFormats.length > 0 &&
    isNotBlankString(form.contactFullName) &&
    isNotBlankString(form.phone) &&
    isNotBlankString(form.contactEmail) &&
    isNotBlankString(form.educationProgramDescription) &&
    form.privacy,
);

const hasValidationErrors = computed(() => !isValidEmail(form.contactEmail));
const isSubmitDisabled = computed(
  () => isSubmitting.value || !isRequiredFieldsFilled.value || hasValidationErrors.value,
);

const rules: Record<string, Rule[]> = {
  fullLegalName: [{ required: true, whitespace: true, message: 'Введите полное название' }],
  shortLegalName: [{ required: true, whitespace: true, message: 'Введите сокращенное название' }],
  organizationalLegalForm: [
    { required: true, whitespace: true, message: 'Введите организационно-правовую форму' },
  ],
  locationAddress: [{ required: true, whitespace: true, message: 'Введите адрес местонахождения' }],
  ogrnOrOgrnip: [{ required: true, whitespace: true, message: 'Введите ОГРН/ОГРНИП' }],
  registrationDate: [{ required: true, message: 'Выберите дату регистрации' }],
  registrationAuthority: [
    { required: true, whitespace: true, message: 'Введите наименование органа' },
  ],
  educationTypesAndLevels: [
    { required: true, type: 'array', min: 1, message: 'Выберите хотя бы один вариант' },
  ],
  educationActivityFormats: [
    { required: true, type: 'array', min: 1, message: 'Выберите хотя бы одну форму' },
  ],
  contactFullName: [{ required: true, whitespace: true, message: 'Введите ФИО' }],
  phone: [{ required: true, whitespace: true, message: 'Введите телефон' }],
  contactEmail: [{ required: true, type: 'email', message: 'Введите корректный e-mail' }],
  educationProgramDescription: [
    { required: true, whitespace: true, message: 'Заполните описание программы' },
  ],
};

const showModal = () => {
  openModal.value = true;
};

const showDrawer = () => {
  resetFormState();
  drawerRenderKey.value += 1;
  open.value = true;
};

const onEducationPermissionOpen = () => {
  showDrawer();
};

const fetchEducationFormMeta = async () => {
  try {
    const response = await fetch(EDUCATION_FORM_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
    });

    const result = (await response.json()) as EducationFormMetaResponse;

    if (!response.ok || result.status !== 'success') {
      throw new Error(result.error ?? 'Не удалось загрузить данные формы');
    }

    const privacyList = result.msg?.privacy ?? [];

    educationTypesAndLevelsOptions.value = mapMetaToOptions(result.msg?.education_types);
    educationActivityFormatOptions.value = mapMetaToOptions(result.msg?.implementation_form);
    privacyOptionId.value = privacyList[0]?.id ?? '';
  } catch {
    message.error('Не удалось загрузить данные формы');
  }
};

onMounted(() => {
  void fetchEducationFormMeta();
  window.addEventListener(EDUCATION_PERMISSION_OPEN_EVENT, onEducationPermissionOpen);
});

onUnmounted(() => {
  window.removeEventListener(EDUCATION_PERMISSION_OPEN_EVENT, onEducationPermissionOpen);
});

const resetFormState = () => {
  Object.assign(form, initialFormState());
  documentItems.value = [];
  documentsUploadRef.value?.reset();
  nextTick(() => {
    generalFormRef.value?.clearValidate();
    contactFormRef.value?.clearValidate();
  });
};

const scrollDrawerToTop = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      const drawerBody = drawerContentRef.value?.closest('.ant-drawer-body');
      drawerBody?.scrollTo({ top: 0, left: 0 });
    });
  });
};

const onAfterOpenChange = (isOpen: boolean) => {
  if (isOpen) {
    scrollDrawerToTop();
  }
};

const onClose = () => {
  resetFormState();
  open.value = false;
};

const parseContactNameParts = (fullName: string): [string, string, string] => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const lastName = parts[0] ?? '';
  const firstName = parts[1] ?? '';
  const middleName = parts.slice(2).join(' ');
  return [lastName, firstName, middleName];
};

const onSubmit = async () => {
  if (!generalFormRef.value || !contactFormRef.value) {
    return;
  }

  try {
    await Promise.all([generalFormRef.value.validate(), contactFormRef.value.validate()]);
  } catch {
    return;
  }

  if (form.privacy && !privacyOptionId.value) {
    message.error('Не удалось определить согласие на обработку данных');
    return;
  }

  isSubmitting.value = true;
  documentsUploadRef.value?.setSubmittingState(true);

  try {
    const readyFiles = documentsUploadRef.value?.getReadyFiles() ?? [];

    const submitResult = await submitFormWithCaptcha({
      endpoint: EDUCATION_FORM_ENDPOINT,
      buildFormData: () => {
        const body = new FormData();
        const [contactLastName, contactFirstName, contactMiddleName] = parseContactNameParts(
          form.contactFullName,
        );

        body.append('full_name', form.fullLegalName);
        body.append('short_name', form.shortLegalName);
        body.append('legal_form', form.organizationalLegalForm);
        body.append('address', form.locationAddress);
        body.append('ogrn', form.ogrnOrOgrnip);
        if (form.registrationDate) {
          body.append('registration_date', form.registrationDate.format('YYYY-MM-DD'));
        }
        body.append('registration_authority', form.registrationAuthority);

        form.educationTypesAndLevels.forEach((value) => {
          body.append('education_types[]', value);
        });
        form.educationActivityFormats.forEach((value) => {
          body.append('implementation_form[]', value);
        });

        body.append('last_name', contactLastName);
        body.append('first_name', contactFirstName);
        body.append('middle_name', contactMiddleName);
        body.append('email', form.contactEmail);
        body.append('phone', form.phone);
        body.append('program_description', form.educationProgramDescription);
        if (form.privacy && privacyOptionId.value) {
          body.append('privacy', privacyOptionId.value);
        }

        readyFiles.forEach((file) => {
          body.append('documents[]', file);
        });

        return body;
      },
    });

    if (!submitResult.ok || !submitResult.data) {
      if (submitResult.errorCode === 'captcha-not-loaded') {
        message.error(getRecaptchaErrorMessage('captcha-not-loaded'));
        return;
      }

      if (submitResult.errorCode === 'captcha-failed') {
        message.error(getRecaptchaErrorMessage('captcha-failed'));
        return;
      }

      message.error(submitResult.message ?? 'Не удалось отправить заявку');
      return;
    }

    successModalDescription.value = submitResult.data.msg ?? '';
    trackYaGoal('submitApplication');
    onClose();
    showModal();
  } catch {
    message.error('Ошибка соединения');
  } finally {
    isSubmitting.value = false;
    documentsUploadRef.value?.resetSubmittingState();
  }
};
</script>
